import React, { useState, useMemo, useCallback } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Dimensions,
  TouchableOpacity,
} from 'react-native';
import {
  Text,
  Card,
  useTheme,
  Chip,
  Button,
  ProgressBar,
  Surface,
  IconButton,
  Divider,
} from 'react-native-paper';
import type { MasteryState } from '@coffeeplexity/shared';

const { width: screenWidth } = Dimensions.get('window');

interface PeerBenchmark {
  skillId: string;
  skillName: string;
  userMastery: number;
  peerAverage: number;
  percentile: number;
  sampleSize: number;
  category: string;
}

interface MasteryComparisonToolsProps {
  masteryStates: MasteryState[];
  userId?: string;
  onSkillSelect: (skillId: string) => void;
  style?: any;
}

type ComparisonMode = 'peer' | 'historical' | 'category' | 'goals';

export function MasteryComparisonTools({
  masteryStates,
  userId,
  onSkillSelect,
  style,
}: MasteryComparisonToolsProps) {
  const theme = useTheme();
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>('peer');
  const [selectedTimeframe, setSelectedTimeframe] = useState<
    'week' | 'month' | 'quarter'
  >('month');
  const [showOnlyImproving, setShowOnlyImproving] = useState(false);

  // Mock peer benchmark data (in real app, this would come from API)
  const peerBenchmarks: PeerBenchmark[] = useMemo(() => {
    return masteryStates.map(mastery => ({
      skillId: mastery.skillId,
      skillName: mastery.skillName,
      userMastery: mastery.masteryLevel,
      peerAverage: Math.max(
        0.1,
        mastery.masteryLevel + (Math.random() - 0.5) * 0.3
      ),
      percentile: Math.floor(Math.random() * 100),
      sampleSize: Math.floor(Math.random() * 1000) + 100,
      category: mastery.skillCategory,
    }));
  }, [masteryStates]);

  // Mock historical data
  const historicalData = useMemo(() => {
    return masteryStates.map(mastery => ({
      skillId: mastery.skillId,
      skillName: mastery.skillName,
      currentMastery: mastery.masteryLevel,
      previousMastery: Math.max(0, mastery.masteryLevel - Math.random() * 0.2),
      change:
        mastery.masteryLevel -
        Math.max(0, mastery.masteryLevel - Math.random() * 0.2),
      trend: mastery.trend,
    }));
  }, [masteryStates]);

  // Category averages
  const categoryAverages = useMemo(() => {
    const categories: Record<
      string,
      { total: number; count: number; skills: MasteryState[] }
    > = {};

    masteryStates.forEach(mastery => {
      if (!categories[mastery.skillCategory]) {
        categories[mastery.skillCategory] = { total: 0, count: 0, skills: [] };
      }
      categories[mastery.skillCategory].total += mastery.masteryLevel;
      categories[mastery.skillCategory].count += 1;
      categories[mastery.skillCategory].skills.push(mastery);
    });

    return Object.entries(categories).map(([category, data]) => ({
      category,
      average: data.total / data.count,
      skillCount: data.count,
      skills: data.skills,
      topSkill: data.skills.reduce((top, skill) =>
        skill.masteryLevel > top.masteryLevel ? skill : top
      ),
      weakestSkill: data.skills.reduce((weak, skill) =>
        skill.masteryLevel < weak.masteryLevel ? skill : weak
      ),
    }));
  }, [masteryStates]);

  const getPerformanceColor = (percentile: number) => {
    if (percentile >= 80) return '#4CAF50'; // Green - Top 20%
    if (percentile >= 60) return '#8BC34A'; // Light Green - Top 40%
    if (percentile >= 40) return '#FFC107'; // Amber - Middle
    if (percentile >= 20) return '#FF9800'; // Orange - Bottom 40%
    return '#F44336'; // Red - Bottom 20%
  };

  const getChangeColor = (change: number) => {
    if (change > 0.05) return '#4CAF50'; // Significant improvement
    if (change > 0) return '#8BC34A'; // Slight improvement
    if (change === 0) return '#9E9E9E'; // No change
    if (change > -0.05) return '#FF9800'; // Slight decline
    return '#F44336'; // Significant decline
  };

  const renderPeerComparison = () => (
    <ScrollView style={styles.scrollContainer}>
      <View style={styles.sectionHeader}>
        <Text variant='titleMedium' style={styles.sectionTitle}>
          Peer Benchmarking
        </Text>
        <Text variant='bodySmall' style={styles.sectionDescription}>
          Compare your skills with anonymized peer data
        </Text>
      </View>

      {peerBenchmarks
        .filter(
          benchmark =>
            !showOnlyImproving || benchmark.userMastery > benchmark.peerAverage
        )
        .map(benchmark => (
          <TouchableOpacity
            key={benchmark.skillId}
            onPress={() => onSkillSelect(benchmark.skillId)}
          >
            <Card style={styles.comparisonCard} mode='outlined'>
              <Card.Content style={styles.cardContent}>
                <View style={styles.skillHeader}>
                  <View style={styles.skillInfo}>
                    <Text variant='titleSmall' style={styles.skillName}>
                      {benchmark.skillName}
                    </Text>
                    <Text variant='bodySmall' style={styles.skillCategory}>
                      {benchmark.category}
                    </Text>
                  </View>

                  <Chip
                    style={[
                      styles.percentileChip,
                      {
                        backgroundColor:
                          getPerformanceColor(benchmark.percentile) + '20',
                      },
                    ]}
                    textStyle={{
                      color: getPerformanceColor(benchmark.percentile),
                    }}
                    compact
                  >
                    {benchmark.percentile}th percentile
                  </Chip>
                </View>

                <View style={styles.comparisonBars}>
                  {/* User Progress */}
                  <View style={styles.progressRow}>
                    <Text variant='bodySmall' style={styles.progressLabel}>
                      You ({Math.round(benchmark.userMastery * 100)}%)
                    </Text>
                    <ProgressBar
                      progress={benchmark.userMastery}
                      color={theme.colors.primary}
                      style={styles.progressBar}
                    />
                  </View>

                  {/* Peer Average */}
                  <View style={styles.progressRow}>
                    <Text variant='bodySmall' style={styles.progressLabel}>
                      Peers ({Math.round(benchmark.peerAverage * 100)}%)
                    </Text>
                    <ProgressBar
                      progress={benchmark.peerAverage}
                      color={theme.colors.outline}
                      style={styles.progressBar}
                    />
                  </View>
                </View>

                <View style={styles.benchmarkStats}>
                  <Text variant='bodySmall' style={styles.statText}>
                    Based on {benchmark.sampleSize.toLocaleString()} learners
                  </Text>
                  <Text
                    variant='bodySmall'
                    style={[
                      styles.differenceText,
                      {
                        color:
                          benchmark.userMastery > benchmark.peerAverage
                            ? '#4CAF50'
                            : '#FF9800',
                      },
                    ]}
                  >
                    {benchmark.userMastery > benchmark.peerAverage ? '+' : ''}
                    {Math.round(
                      (benchmark.userMastery - benchmark.peerAverage) * 100
                    )}
                    % vs peers
                  </Text>
                </View>
              </Card.Content>
            </Card>
          </TouchableOpacity>
        ))}
    </ScrollView>
  );

  const renderHistoricalComparison = () => (
    <ScrollView style={styles.scrollContainer}>
      <View style={styles.sectionHeader}>
        <Text variant='titleMedium' style={styles.sectionTitle}>
          Progress Over Time
        </Text>
        <Text variant='bodySmall' style={styles.sectionDescription}>
          Track your skill development over the past {selectedTimeframe}
        </Text>
      </View>

      <View style={styles.timeframeSelector}>
        {(['week', 'month', 'quarter'] as const).map(timeframe => (
          <Chip
            key={timeframe}
            selected={selectedTimeframe === timeframe}
            onPress={() => setSelectedTimeframe(timeframe)}
            style={styles.timeframeChip}
          >
            {timeframe.charAt(0).toUpperCase() + timeframe.slice(1)}
          </Chip>
        ))}
      </View>

      {historicalData
        .filter(data => !showOnlyImproving || data.change > 0)
        .map(data => (
          <TouchableOpacity
            key={data.skillId}
            onPress={() => onSkillSelect(data.skillId)}
          >
            <Card style={styles.comparisonCard} mode='outlined'>
              <Card.Content style={styles.cardContent}>
                <View style={styles.skillHeader}>
                  <Text variant='titleSmall' style={styles.skillName}>
                    {data.skillName}
                  </Text>

                  <View style={styles.changeIndicator}>
                    <Text
                      style={[
                        styles.changeText,
                        { color: getChangeColor(data.change) },
                      ]}
                    >
                      {data.change > 0 ? '+' : ''}
                      {Math.round(data.change * 100)}%
                    </Text>
                    <Text
                      style={[
                        styles.changeIcon,
                        { color: getChangeColor(data.change) },
                      ]}
                    >
                      {data.change > 0 ? '↗' : data.change < 0 ? '↘' : '→'}
                    </Text>
                  </View>
                </View>

                <View style={styles.comparisonBars}>
                  <View style={styles.progressRow}>
                    <Text variant='bodySmall' style={styles.progressLabel}>
                      Current ({Math.round(data.currentMastery * 100)}%)
                    </Text>
                    <ProgressBar
                      progress={data.currentMastery}
                      color={theme.colors.primary}
                      style={styles.progressBar}
                    />
                  </View>

                  <View style={styles.progressRow}>
                    <Text variant='bodySmall' style={styles.progressLabel}>
                      Previous ({Math.round(data.previousMastery * 100)}%)
                    </Text>
                    <ProgressBar
                      progress={data.previousMastery}
                      color={theme.colors.outline}
                      style={styles.progressBar}
                    />
                  </View>
                </View>
              </Card.Content>
            </Card>
          </TouchableOpacity>
        ))}
    </ScrollView>
  );

  const renderCategoryComparison = () => (
    <ScrollView style={styles.scrollContainer}>
      <View style={styles.sectionHeader}>
        <Text variant='titleMedium' style={styles.sectionTitle}>
          Category Overview
        </Text>
        <Text variant='bodySmall' style={styles.sectionDescription}>
          Compare mastery across different skill categories
        </Text>
      </View>

      {categoryAverages.map(categoryData => (
        <Card
          key={categoryData.category}
          style={styles.categoryCard}
          mode='outlined'
        >
          <Card.Content style={styles.cardContent}>
            <View style={styles.categoryHeader}>
              <Text variant='titleMedium' style={styles.categoryName}>
                {categoryData.category}
              </Text>
              <Text
                variant='headlineSmall'
                style={[
                  styles.categoryAverage,
                  { color: getPerformanceColor(categoryData.average * 100) },
                ]}
              >
                {Math.round(categoryData.average * 100)}%
              </Text>
            </View>

            <View style={styles.categoryStats}>
              <Text variant='bodySmall' style={styles.statText}>
                {categoryData.skillCount} skills in this category
              </Text>
            </View>

            <Divider style={styles.divider} />

            <View style={styles.categorySkills}>
              <View style={styles.skillComparison}>
                <Text variant='bodySmall' style={styles.comparisonLabel}>
                  Strongest: {categoryData.topSkill.skillName}
                </Text>
                <ProgressBar
                  progress={categoryData.topSkill.masteryLevel}
                  color='#4CAF50'
                  style={styles.comparisonBar}
                />
                <Text variant='bodySmall' style={styles.comparisonValue}>
                  {Math.round(categoryData.topSkill.masteryLevel * 100)}%
                </Text>
              </View>

              <View style={styles.skillComparison}>
                <Text variant='bodySmall' style={styles.comparisonLabel}>
                  Needs work: {categoryData.weakestSkill.skillName}
                </Text>
                <ProgressBar
                  progress={categoryData.weakestSkill.masteryLevel}
                  color='#FF9800'
                  style={styles.comparisonBar}
                />
                <Text variant='bodySmall' style={styles.comparisonValue}>
                  {Math.round(categoryData.weakestSkill.masteryLevel * 100)}%
                </Text>
              </View>
            </View>

            <Button
              mode='outlined'
              onPress={() => onSkillSelect(categoryData.weakestSkill.skillId)}
              style={styles.focusButton}
              compact
            >
              Focus on improvement
            </Button>
          </Card.Content>
        </Card>
      ))}
    </ScrollView>
  );

  const renderGoalsComparison = () => (
    <ScrollView style={styles.scrollContainer}>
      <View style={styles.sectionHeader}>
        <Text variant='titleMedium' style={styles.sectionTitle}>
          Goal Progress
        </Text>
        <Text variant='bodySmall' style={styles.sectionDescription}>
          Track progress toward your learning goals
        </Text>
      </View>

      <Card style={styles.goalsCard} mode='outlined'>
        <Card.Content>
          <Text variant='titleMedium' style={styles.goalsTitle}>
            Overall Mastery Goal: 75%
          </Text>

          <View style={styles.goalProgress}>
            <ProgressBar
              progress={
                masteryStates.reduce((sum, m) => sum + m.masteryLevel, 0) /
                masteryStates.length
              }
              color={theme.colors.primary}
              style={styles.goalProgressBar}
            />
            <Text variant='bodyMedium' style={styles.goalPercentage}>
              {Math.round(
                (masteryStates.reduce((sum, m) => sum + m.masteryLevel, 0) /
                  masteryStates.length) *
                  100
              )}
              %
            </Text>
          </View>

          <Text variant='bodySmall' style={styles.goalDescription}>
            You're{' '}
            {Math.round(
              (0.75 -
                masteryStates.reduce((sum, m) => sum + m.masteryLevel, 0) /
                  masteryStates.length) *
                100
            )}
            % away from your goal
          </Text>
        </Card.Content>
      </Card>

      <Text variant='titleSmall' style={styles.subsectionTitle}>
        Skills Closest to Goals
      </Text>

      {masteryStates
        .filter(mastery => mastery.masteryLevel >= 0.6)
        .sort((a, b) => b.masteryLevel - a.masteryLevel)
        .slice(0, 5)
        .map(mastery => (
          <TouchableOpacity
            key={mastery.id}
            onPress={() => onSkillSelect(mastery.skillId)}
          >
            <Card style={styles.goalSkillCard} mode='outlined'>
              <Card.Content style={styles.goalSkillContent}>
                <Text variant='titleSmall'>{mastery.skillName}</Text>
                <View style={styles.goalSkillProgress}>
                  <ProgressBar
                    progress={mastery.masteryLevel}
                    color='#4CAF50'
                    style={styles.goalSkillBar}
                  />
                  <Text variant='bodySmall' style={styles.goalSkillPercentage}>
                    {Math.round(mastery.masteryLevel * 100)}%
                  </Text>
                </View>
              </Card.Content>
            </Card>
          </TouchableOpacity>
        ))}
    </ScrollView>
  );

  const renderContent = () => {
    switch (comparisonMode) {
      case 'peer':
        return renderPeerComparison();
      case 'historical':
        return renderHistoricalComparison();
      case 'category':
        return renderCategoryComparison();
      case 'goals':
        return renderGoalsComparison();
      default:
        return renderPeerComparison();
    }
  };

  return (
    <Surface style={[styles.container, style]} elevation={1}>
      {/* Mode Selector */}
      <View style={styles.header}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.modeSelector}
        >
          {(
            [
              { mode: 'peer', label: 'Peer Comparison' },
              { mode: 'historical', label: 'Progress' },
              { mode: 'category', label: 'Categories' },
              { mode: 'goals', label: 'Goals' },
            ] as const
          ).map(({ mode, label }) => (
            <Chip
              key={mode}
              selected={comparisonMode === mode}
              onPress={() => setComparisonMode(mode)}
              style={styles.modeChip}
            >
              {label}
            </Chip>
          ))}
        </ScrollView>

        {/* Filter Toggle */}
        <View style={styles.filterRow}>
          <Chip
            selected={showOnlyImproving}
            onPress={() => setShowOnlyImproving(!showOnlyImproving)}
            style={styles.filterChip}
            icon={showOnlyImproving ? 'check' : undefined}
          >
            Show only improving
          </Chip>
        </View>
      </View>

      {/* Content */}
      <View style={styles.content}>{renderContent()}</View>
    </Surface>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    borderRadius: 12,
  },
  header: {
    padding: 16,
    paddingBottom: 8,
  },
  modeSelector: {
    marginBottom: 12,
  },
  modeChip: {
    marginRight: 8,
  },
  filterRow: {
    flexDirection: 'row',
  },
  filterChip: {
    marginRight: 8,
  },
  content: {
    flex: 1,
  },
  scrollContainer: {
    flex: 1,
  },
  sectionHeader: {
    padding: 16,
    paddingBottom: 8,
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: 4,
  },
  sectionDescription: {
    opacity: 0.7,
  },
  comparisonCard: {
    marginHorizontal: 16,
    marginBottom: 8,
  },
  cardContent: {
    padding: 16,
  },
  skillHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  skillInfo: {
    flex: 1,
  },
  skillName: {
    fontWeight: '600',
  },
  skillCategory: {
    opacity: 0.7,
    textTransform: 'capitalize',
  },
  percentileChip: {
    height: 28,
  },
  comparisonBars: {
    marginBottom: 12,
  },
  progressRow: {
    marginBottom: 8,
  },
  progressLabel: {
    marginBottom: 4,
    fontWeight: '500',
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
  },
  benchmarkStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statText: {
    opacity: 0.7,
  },
  differenceText: {
    fontWeight: '600',
  },
  changeIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  changeText: {
    fontWeight: '600',
  },
  changeIcon: {
    fontSize: 16,
  },
  timeframeSelector: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    marginBottom: 16,
    gap: 8,
  },
  timeframeChip: {
    flex: 1,
  },
  categoryCard: {
    marginHorizontal: 16,
    marginBottom: 12,
  },
  categoryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  categoryName: {
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  categoryAverage: {
    fontWeight: '700',
  },
  categoryStats: {
    marginBottom: 12,
  },
  divider: {
    marginVertical: 12,
  },
  categorySkills: {
    marginBottom: 12,
  },
  skillComparison: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 8,
  },
  comparisonLabel: {
    flex: 1,
    fontWeight: '500',
  },
  comparisonBar: {
    flex: 2,
    height: 4,
  },
  comparisonValue: {
    minWidth: 35,
    textAlign: 'right',
    fontWeight: '600',
  },
  focusButton: {
    alignSelf: 'flex-start',
  },
  goalsCard: {
    marginHorizontal: 16,
    marginBottom: 16,
  },
  goalsTitle: {
    fontWeight: '600',
    marginBottom: 12,
  },
  goalProgress: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  goalProgressBar: {
    flex: 1,
    height: 8,
    borderRadius: 4,
  },
  goalPercentage: {
    fontWeight: '600',
    minWidth: 40,
  },
  goalDescription: {
    opacity: 0.7,
  },
  subsectionTitle: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    fontWeight: '600',
  },
  goalSkillCard: {
    marginHorizontal: 16,
    marginBottom: 6,
  },
  goalSkillContent: {
    padding: 12,
  },
  goalSkillProgress: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  goalSkillBar: {
    flex: 1,
    height: 4,
  },
  goalSkillPercentage: {
    minWidth: 35,
    textAlign: 'right',
    fontWeight: '500',
  },
});
